import React, { useState } from "react";
import {
  IconButton,
  Tooltip,
} from "@material-tailwind/react";
import { WorkorderStatus } from "../../types/workorder/workorderStatus";
import { getWorkorderStatusStyle } from "../data/workorder-status/workorderStatusStyles";
import { t } from "i18next";
import { api } from "../../services/api/api";
import {
  downloadWorkorderReport,
  previewWorkorderReport,
} from "../../services/export/workorderReport";
//import { useWorkorderContext } from "../../context/WorkorderContext";
import toast from "react-hot-toast";
import { UserRole } from "../../hooks/useWorkorderPermissions";
import StatusChangeConfirmDialog from "../data/workorder-status/StatusChangeConfirmDialog";
import { useKeycloak } from "@react-keycloak/web";

/**
 * Mirrors the server's own resume-eligibility set (core: the statuses SUSPENDED is allowed to
 * resume back to). ACCEPTED, IN_TRANSIT and IN_PROGRESS are treated as a genuine resume; CREATED
 * is not -- PUT .../status/CREATED nulls the recorded time, nulls the safety-diag status and
 * retires the diagnosis, the same destructive reset as the existing "new" button. CANCELED is
 * likewise not a resume.
 *
 * If the server's set changes, this one has to change with it -- offering a status here that the
 * server does not treat as a resume mislabels the destructive reset as "Resume".
 */
const RESUME_TARGETS: readonly WorkorderStatus[] = [
  WorkorderStatus.ACCEPTED,
  WorkorderStatus.IN_TRANSIT,
  WorkorderStatus.IN_PROGRESS,
];

/**
 * Interface de propriétés pour le composant StatusChangeMenu
 */
interface StatusChangeMenuProps {
  /** ID du technicien associé au workorder */
  id: string;
  /** ID du workorder à modifier */
  workorderId: string;
  /** Référence du workorder */
  workorderRef: string;
  /** Statut actuel du workorder */
  currentStatus: string;
  /** Fonction de callback appelée après un changement de statut réussi */
  onStatusChange?: () => void | Promise<void>;
  /** Objet workorder complet (optionnel pour éviter un fetch) */
  workorder?: EasyTech.Workorder;
  /** Éléments enfants pour personnaliser le bouton du menu */
  children?: React.ReactNode;
}

/**
 * Composant qui affiche des boutons pour changer le statut d'un workorder
 * en fonction des permissions de l'utilisateur et des transitions possibles.
 */
export const StatusChangeMenu: React.FC<StatusChangeMenuProps> = ({
  id,
  workorderId,
  workorderRef,
  currentStatus,
  onStatusChange,
  workorder: initialWorkorder,
}) => {
  const [loading, setLoading] = useState(false);
  const [workorder, setWorkorder] = useState<EasyTech.Workorder | null>(initialWorkorder || null);
  const [customerDetail, setCustomerDetail] = useState<EasyTech.Customer | null>(null);
  const [customerLanguages, setCustomerLanguages] = useState<any[]>([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState<boolean>(false);
  // Seeded from the customer each time the validation dialog opens (see fetchCustomerDetail).
  // Never default it to true: PUT /admin/workorders/{id}/validate treats this value as the sole
  // authority and performs no fallback, so an optimistic default emails an opted-out customer.
  const [sendReportEmail, setSendReportEmail] = useState<boolean>(false);

  // Mettre à jour workorder si initialWorkorder change
  React.useEffect(() => {
    if (initialWorkorder) {
      setWorkorder(initialWorkorder);
    }
  }, [initialWorkorder]);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    status: WorkorderStatus | null;
    statusBgClass: string;
    statusIcon: React.ElementType | null;
  }>({
    open: false,
    status: null,
    statusBgClass: "",
    statusIcon: null,
  });
  const { keycloak } = useKeycloak();

  /**
   * Récupère les détails du workorder
   */
  const fetchWorkorder = async () => {
    try {
      const response = await api.get(`users/workorders/${workorderId}`) as EasyTech.Workorder;
      if (response) {
        setWorkorder(response);
        return response;
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du workorder:", error);
    }
    return null;
  };

  /**
   * Récupère le détail du client et en déduit l'état par défaut du toggle d'envoi de rapport.
   *
   * workorder.customer is a CustomerSimpleResponse: it carries neither sendInterventionReportEmail
   * nor the full language list, so the preference cannot be read off the workorder. The detailed
   * endpoint returns both, in the same shape as admin/customers/{id}/languages, so one unrestricted
   * call replaces the previous admin-only one.
   *
   * Anything other than an explicit `true` -- false, absent, or a failed request -- leaves the
   * toggle off. The validate endpoint takes this value at face value.
   */
  /** Single place where a detailed customer becomes state, whether it was read or just written. */
  const applyCustomerDetail = (detail: EasyTech.Customer) => {
    setCustomerDetail(detail);
    setCustomerLanguages(Array.isArray(detail?.languages) ? detail.languages : []);
    setSendReportEmail(detail?.sendInterventionReportEmail === true);
  };

  const fetchCustomerDetail = async (customerId: string) => {
    try {
      setIsLoadingLanguages(true);
      const response = await api.get(`users/customers/${customerId}`) as EasyTech.Customer;
      applyCustomerDetail(response);
      return response;
    } catch (error) {
      console.error("Erreur lors de la récupération du customer:", error);
      setCustomerDetail(null);
      setCustomerLanguages([]);
      setSendReportEmail(false);
      return null;
    } finally {
      setIsLoadingLanguages(false);
    }
  };

  const handleViewPdf = async (languageIsoCode: string) => {
    try {
      await previewWorkorderReport({
        workorderId,
        workorderRef,
        languageIsoCode,
        includeImages: true,
        compactLayout: true,
      });

      toast.success(t("workorder:report:viewSuccess"));
    } catch (error) {
      console.error("Erreur lors de la visualisation du PDF:", error);
      toast.error(t("workorder:report:viewError"));
    }
  };

  const handleDownloadPdf = async (languageIsoCode: string) => {
    try {
      await downloadWorkorderReport({
        workorderId,
        workorderRef,
        languageIsoCode,
        includeImages: true,
        compactLayout: true,
      });

      toast.success(t("workorder:report:downloadSuccess"));
    } catch (error) {
      console.error("Erreur lors du téléchargement du PDF:", error);
      toast.error(t("workorder:report:downloadError"));
    }
  };

  const handleEditCustomer = () => {
    closeConfirmDialog();
  };

  const handleCustomerUpdate = async (updatedCustomer: EasyTech.Customer) => {
    try {
      // The update answers with the detailed customer, so it re-seeds the toggle from the
      // preference just saved without a re-read.
      const saved = await api.put(`admin/customers/${updatedCustomer.id}`, updatedCustomer) as EasyTech.Customer;
      toast.success(t("client:updateSuccess"));
      applyCustomerDetail(saved);

      // The workorder still needs refreshing on its own: the dialog reads the recipient emails off
      // workorder.customer, which this PUT does not touch.
      await fetchWorkorder();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du customer:", error);
      toast.error(t("client:updateError"));
      throw error;
    }
  };

  // Vérification des rôles de l'utilisateur
  const isAdmin = keycloak.realmAccess?.roles.includes(UserRole.ADMIN);
  const isSuperAdmin = keycloak.realmAccess?.roles.includes(UserRole.SUPER_ADMIN);
  const isPlanner = keycloak.realmAccess?.roles.includes(UserRole.PLANNER);
  const isTech = keycloak.realmAccess?.roles.includes(UserRole.TECHNICIAN);

  /**
   * Détermine les statuts disponibles pour le changement en fonction
   * du statut actuel et des rôles de l'utilisateur
   * @returns Liste des statuts disponibles pour la transition
   */
  const getAvailableStatuses = (): WorkorderStatus[] => {
    // Vérifier si un technicien est assigné (id est l'ID du technicien)
    // const hasTechnician = id !== undefined && id !== null && id.trim() !== "";

    if (currentStatus === WorkorderStatus.CREATED) {
      if (isTech) {
        return [WorkorderStatus.ACCEPTED, WorkorderStatus.SUSPENDED];
      }
      if (isSuperAdmin) {
        // SuperAdmin peut accepter une intervention (seulement si technicien assigné) et suspendre/annuler
        const statuses = [WorkorderStatus.ACCEPTED, WorkorderStatus.SUSPENDED, WorkorderStatus.CANCELED];
        return statuses;
      }
      if (isPlanner || isAdmin) {
        return [WorkorderStatus.SUSPENDED, WorkorderStatus.CANCELED];
      }
    } else if (currentStatus === WorkorderStatus.ACCEPTED) {
      if (isTech) {
        return [WorkorderStatus.IN_TRANSIT, WorkorderStatus.SUSPENDED];
      }
      if (isSuperAdmin) {
        // SuperAdmin peut passer en route et suspendre
        return [WorkorderStatus.IN_TRANSIT, WorkorderStatus.SUSPENDED];
      }
    } else if (currentStatus === WorkorderStatus.IN_TRANSIT) {
      if (isTech) {
        return [WorkorderStatus.IN_PROGRESS, WorkorderStatus.SUSPENDED];
      }
      if (isSuperAdmin) {
        // SuperAdmin peut démarrer l'intervention et suspendre
        return [WorkorderStatus.IN_PROGRESS, WorkorderStatus.SUSPENDED];
      }
    } else if (currentStatus === WorkorderStatus.IN_PROGRESS) {
      if (isTech) {
        return [WorkorderStatus.CLOSED, WorkorderStatus.SUSPENDED];
      }
      if (isSuperAdmin) {
        // SuperAdmin peut clôturer l'intervention et suspendre
        return [WorkorderStatus.CLOSED, WorkorderStatus.SUSPENDED];
      }
    } else if (currentStatus === WorkorderStatus.CLOSED) {
      if (isSuperAdmin) {
        // SuperAdmin peut suspendre et annuler une intervention clôturée
        return [WorkorderStatus.VALIDATED, WorkorderStatus.SUSPENDED, WorkorderStatus.CANCELED];
      }
      if (isPlanner || isAdmin) {
        // Planner et Admin ne peuvent pas suspendre une intervention clôturée
        return [WorkorderStatus.VALIDATED, WorkorderStatus.CANCELED];
      }
    } else if (currentStatus === WorkorderStatus.SUSPENDED) {
      // Le serveur valide une reprise contre le suspendedFrom enregistré et refuse toute autre
      // cible : on ne propose donc que ce statut-là, jamais un autre. Une pause enregistrée avant
      // l'arrivée de ce champ n'a pas de suspendedFrom -- dans ce cas on ne propose aucune reprise.
      //
      // suspendedFrom is not automatically a resume target, though: a workorder can be suspended
      // straight from CREATED (this same menu offers CREATED -> SUSPENDED), and SUSPENDED ->
      // CREATED is the destructive reset, not a resume -- pushing it here would both duplicate the
      // CREATED entry already in the arrays below and relabel that destructive button "Resume".
      // Only offer it when it is one of RESUME_TARGETS, the set the server actually honors.
      const resumeTarget = workorder?.suspendedFrom as WorkorderStatus | null | undefined;
      const canResumeInPlace = !!resumeTarget && RESUME_TARGETS.includes(resumeTarget);

      if (isSuperAdmin) {
        // SuperAdmin peut fermer une intervention suspendue
        const statuses = [WorkorderStatus.CREATED, WorkorderStatus.CLOSED, WorkorderStatus.CANCELED];
        if (canResumeInPlace) statuses.push(resumeTarget as WorkorderStatus);
        return statuses;
      }
      if (isPlanner || isAdmin) {
        // Planner et Admin ne peuvent pas fermer une intervention suspendue
        const statuses = [WorkorderStatus.CREATED, WorkorderStatus.CANCELED];
        if (canResumeInPlace) statuses.push(resumeTarget as WorkorderStatus);
        return statuses;
      }
    } else if (currentStatus === WorkorderStatus.VALIDATED) {
      if (isSuperAdmin) {
        // SuperAdmin peut annuler une intervention validée
        return [WorkorderStatus.INVOICED, WorkorderStatus.CLOSED, WorkorderStatus.CANCELED];
      }
      if (isPlanner || isAdmin) {
        // Planner et Admin ne peuvent pas annuler une intervention validée
        return [WorkorderStatus.INVOICED, WorkorderStatus.CLOSED];
      }
    } else if (currentStatus === WorkorderStatus.INVOICED) {
      if (isPlanner || isAdmin || isSuperAdmin) {
        return [WorkorderStatus.PAID, WorkorderStatus.VALIDATED];
      }
    } else if (currentStatus === WorkorderStatus.PAID) {
      if (isPlanner || isAdmin || isSuperAdmin) {
        return [WorkorderStatus.INVOICED];
      }
    } else if (currentStatus === WorkorderStatus.CANCELED) {
      if (isSuperAdmin) {
        // SuperAdmin peut changer le statut depuis CANCELED vers SUSPENDED, CREATED, VALIDATED et CLOSED
        // Ordre : SUSPENDED, CREATED, VALIDATED, CLOSED (de gauche à droite)
        return [WorkorderStatus.CREATED, WorkorderStatus.CLOSED, WorkorderStatus.VALIDATED, WorkorderStatus.SUSPENDED];
      }
    }

    return [];
  };

  const availableStatuses = getAvailableStatuses();

  /**
   * Change le statut du workorder via l'API
   * @param newStatus Nouveau statut à appliquer
   */
  const changeStatus = async (newStatus: WorkorderStatus) => {
    try {
      setLoading(true);
      
      if (newStatus === WorkorderStatus.VALIDATED) {
        await api.put(`admin/workorders/${workorderId}/validate`, {
          sendReportEmail: sendReportEmail
        });
      } else {
        await api.put(`users/workorders/${workorderId}/status/${newStatus}`, {});
      }
      
      toast.success(t("workorder:logs:statusChanged"));

      if (onStatusChange) {
        await onStatusChange();
      }
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
      toast.error(t("workorder:status:changed_error"));
    } finally {
      setLoading(false);
      closeConfirmDialog();
    }
  };

  /**
   * Ouvre le dialog de confirmation pour changer le statut.
   * La couleur et l'icône se déduisent du statut (workorderStatusStyles), elles
   * ne sont donc pas passées par l'appelant.
   * @param status Le nouveau statut à appliquer
   */
  const openConfirmDialog = async (status: WorkorderStatus) => {
    if (status === WorkorderStatus.VALIDATED) {
      let wo = workorder;
      if (!wo) {
        wo = await fetchWorkorder();
      }

      // Not awaited: the dialog opens straight away and gates its report section on
      // isLoadingLanguages, so the toggle is never shown in a state the customer has not confirmed.
      if (wo?.customer?.id) {
        void fetchCustomerDetail(wo.customer.id);
      }
    }

    const style = getWorkorderStatusStyle(status);
    setConfirmDialog({
      open: true,
      status,
      statusBgClass: style.solidClass,
      statusIcon: style.icon,
    });
  };

  /**
   * Ferme le dialog de confirmation
   *
   * The report-email decision belongs to one validation, not to the menu's lifetime: without this
   * reset, turning the toggle off for one intervention carried over to the next one opened from the
   * same list row.
   *
   * Only the decision is reset. The loaded customer is deliberately kept: closing the dialog is also
   * how the "edit customer" shortcut opens (handleEditCustomer), and that drawer is seeded from this
   * very object -- clearing it here would make the seeding depend on closure timing. The next open
   * re-fetches it anyway.
   */
  const closeConfirmDialog = () => {
    setSendReportEmail(false);
    setConfirmDialog({
      open: false,
      status: null,
      statusBgClass: "",
      statusIcon: null,
    });
  };

  /**
   * Confirme le changement de statut
   */
  const handleConfirmStatusChange = () => {
    if (confirmDialog.status) {
      changeStatus(confirmDialog.status);
    }
  };

  // Si aucun statut disponible, afficher un message explicatif
  if (availableStatuses.length === 0) {
    return (
      <div className="text-xs text-gray-500">
        {/** TODO: Message de changement de statut non disponible à afficher ou non ?*/}
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <div className="flex gap-2">
        {availableStatuses.map((status) => {
          const style = getWorkorderStatusStyle(status);
          const StatusIcon = style.icon;

          let tooltipLabel = "";
          switch (status) {
          case WorkorderStatus.CREATED:
            tooltipLabel = t("workorder:action:created");
            break;
          case WorkorderStatus.ACCEPTED:
            tooltipLabel = t("workorder:action:accepted");
            break;
          case WorkorderStatus.IN_TRANSIT:
            tooltipLabel = t("workorder:action:in_transit");
            break;
          case WorkorderStatus.IN_PROGRESS:
            tooltipLabel = t("workorder:action:in_progress");
            break;
          case WorkorderStatus.SUSPENDED:
            tooltipLabel = t("workorder:action:suspended");
            break;
          case WorkorderStatus.CANCELED:
            tooltipLabel = t("workorder:action:canceled");
            break;
          case WorkorderStatus.VALIDATED:
            tooltipLabel = t("workorder:action:validated");
            break;
          case WorkorderStatus.CLOSED:
            tooltipLabel = t("workorder:action:closed");
            break;
          case WorkorderStatus.INVOICED:
            tooltipLabel = t("workorder:action:invoiced");
            break;
          case WorkorderStatus.PAID:
            tooltipLabel = t("workorder:action:paid");
            break;
          default:
            tooltipLabel = status;
          }

          // The resume-in-place button is the one entry in availableStatuses that targets
          // suspendedFrom while currentStatus is still SUSPENDED, AND suspendedFrom is one of
          // RESUME_TARGETS. Its label always reads "resume", regardless of which status it happens
          // to restore to (IN_PROGRESS, ACCEPTED, ...), rather than reusing that status's generic
          // action label (which is not "resume" wording in every locale).
          //
          // The RESUME_TARGETS check is required here too, not just when building
          // availableStatuses: suspendedFrom can equal CREATED (a workorder can be suspended
          // straight from CREATED), and the CREATED entry is always in this list as the destructive
          // reset -- without re-checking membership, that single button would get relabelled
          // "Resume" even though clicking it still performs the destructive reset.
          const isResumeInPlace =
            currentStatus === WorkorderStatus.SUSPENDED &&
            status === (workorder?.suspendedFrom as WorkorderStatus | null | undefined) &&
            RESUME_TARGETS.includes(status);
          const accessibleLabel = isResumeInPlace
            ? t("planification:suspensionReason.resume")
            : t("workorder:status." + status.toLowerCase());
          if (isResumeInPlace) {
            tooltipLabel = accessibleLabel;
          }

          return (
            <Tooltip key={status} content={tooltipLabel}>
              <IconButton
                variant="filled"
                className={`${style.solidClass} hover:shadow-lg`}
                onClick={() => openConfirmDialog(status)}
                disabled={loading}
                aria-label={accessibleLabel}
              >
                {StatusIcon && <StatusIcon className="h-4 w-4 text-white" />}
              </IconButton>
            </Tooltip>
          );
        })}
      </div>

      <StatusChangeConfirmDialog
        open={confirmDialog.open}
        status={confirmDialog.status}
        statusBgClass={confirmDialog.statusBgClass}
        statusIcon={confirmDialog.statusIcon}
        workorderRef={workorderRef}
        onClose={closeConfirmDialog}
        onConfirm={handleConfirmStatusChange}
        loading={loading}
        workorder={workorder || undefined}
        customerDetail={customerDetail}
        customerLanguages={customerLanguages}
        isLoadingLanguages={isLoadingLanguages}
        sendReportEmail={sendReportEmail}
        setSendReportEmail={setSendReportEmail}
        handleViewPdf={handleViewPdf}
        handleDownloadPdf={handleDownloadPdf}
        handleEditCustomer={handleEditCustomer}
        onCustomerUpdate={handleCustomerUpdate}
      />
    </div>
  );
};
